import { useState } from "react";
import { ArrowLeftOutlined } from "@ant-design/icons";
import PersonalInformation from "./PersonalInformation/PersonalInformation";
import RolePermissions from "./RolePermissions/RolePermissions";
import { useGetRoles } from "../../../queries/useRolesApi";
import "./stylesNewStaff.css";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { formatTimezoneDisplay } from "../../../utils/dateService";

function NewStaff({
    onClose,
    onSave,
    isApiSending = false,
}) {
    const { data: apiRoles } = useGetRoles();
    const [currentStep, setCurrentStep] = useState(1);
    const [personalInfo, setPersonalInfo] = useState({
        firstName: "",
        lastName: "",
        email: "",
        timezone: "",
        agentId: "",
        position: "",
    });
    const [roleInfo, setRoleInfo] = useState({
        role: null,
        actions: {},
        permissions: {},
    });

    const handlePersonalInfoChange = (field, value) => {
        setPersonalInfo((prev) => ({ ...prev, [field]: value }));
    };

    const handleRoleChange = (value) => {
        setRoleInfo((prev) => ({ ...prev, role: value }));
    };

    const handleRoleSelected = (payload) => {
        if (payload?.actions != null || payload?.permissions != null) {
            setRoleInfo((prev) => ({
                ...prev,
                actions: payload.actions ?? prev.actions,
                permissions: payload.permissions ?? prev.permissions,
            }));
        }
    };

    const handleActionToggle = (data) => {
        // ToggleSwitch passes { event_code, is_active }
        const action = data?.eventCode || data?.event_code;
        const isChecked = data?.is_active !== undefined ? data.is_active : !roleInfo.actions[action];
        if (action) {
            setRoleInfo((prev) => ({
                ...prev,
                actions: {
                    ...prev.actions,
                    [action]: isChecked,
                },
            }));
        }
    };

    const handleContinue = () => {
        if (currentStep === 1) {
            // Validate personal info
            if (
                personalInfo.firstName &&
                personalInfo.lastName &&
                personalInfo.email
            ) {
                setCurrentStep(2);
            }
        }
    };

    const handleBack = () => {
        if (currentStep === 2) {
            setCurrentStep(1);
        } else {
            onClose();
        }
    };

    const handleSave = () => {
        if (onSave) {
            onSave({
                ...personalInfo,
                ...roleInfo,
            });
        }
    };

    const isStep1Valid =
        personalInfo.firstName &&
        personalInfo.lastName &&
        personalInfo.email;

    const selectedRoleData = apiRoles?.find(
        (r) => r.id === roleInfo.role || r.id === String(roleInfo.role)
    );

    return (
        <div className="new-staff-container">
            <div className="new-staff-title">
                <div className="new-staff-title-left">
                    <button className="new-staff-back-button" onClick={handleBack}>
                        <ArrowLeftOutlined />
                    </button>
                    <h2>{AppStrings.NEW_STAFF}</h2>
                </div>
                <div className="new-staff-action-buttons">
                    <button
                        className="new-staff-cancel-button"
                        onClick={onClose}
                    >
                        {AppStrings.CANCEL}
                    </button>
                    <button
                        className={`new-staff-save-button ${isStep1Valid && roleInfo.role && !isApiSending ? "active" : ""}`}
                        onClick={handleSave}
                        disabled={!isStep1Valid || !roleInfo.role || isApiSending}
                    >
                        {isApiSending ? "Sending..." : AppStrings.SEND_VERIFICATION_EMAIL}
                    </button>
                </div>
            </div>
            <div className="new-staff-content">
                <div className="new-staff-main">
                    <div className="new-staff-steps">
                        <div
                            className={`new-staff-step ${currentStep === 1 ? "active" : ""} clickable`}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                // Always allow navigation to step 1 when clicking
                                setCurrentStep(1);
                            }}
                            style={{ cursor: "pointer" }}
                        >
                            <div className="step-number">1</div>
                            <div className="step-label">{AppStrings.PERSONAL_INFORMATION}</div>
                        </div>
                        <div
                            className={`new-staff-step ${currentStep === 2 ? "active" : ""} clickable`}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                // Always allow navigation to step 2 when clicking
                                setCurrentStep(2);
                            }}
                            style={{ cursor: "pointer" }}
                        >
                            <div className="step-number">2</div>
                            <div className="step-label">{AppStrings.ROLE_AND_PERMISSIONS}</div>
                        </div>
                    </div>

                    {currentStep === 1 && (
                        <div className="new-staff-step-content">
                            <PersonalInformation
                                personalInfo={personalInfo}
                                onPersonalInfoChange={handlePersonalInfoChange}
                                onContinue={handleContinue}
                            />
                        </div>
                    )}

                    {currentStep === 2 && (
                        <div className="new-staff-step-content">
                            <RolePermissions
                                roleInfo={roleInfo}
                                onRoleChange={handleRoleChange}
                                onRoleSelected={handleRoleSelected}
                                onActionToggle={handleActionToggle}
                                isReadOnly={true}
                            />

                        </div>
                    )}
                </div>

                <div className="new-staff-summary">
                    <div className="summary-card">
                        <h3>{AppStrings.PERSONAL_INFO}</h3>
                        <div className="summary-item">
                            <span className="summary-label">{AppStrings.NAME}:</span>
                            <span className="summary-value">
                                {personalInfo.firstName && personalInfo.lastName
                                    ? `${personalInfo.firstName} ${personalInfo.lastName}`
                                    : "-"}
                            </span>
                        </div>
                        <div className="summary-item">
                            <span className="summary-label">{AppStrings.EMAIL}:</span>
                            <span className="summary-value">
                                {personalInfo.email || "-"}
                            </span>
                        </div>
                        <div className="summary-item">
                            <span className="summary-label">Timezone:</span>
                            <span className="summary-value">
                                {personalInfo.timezone ? formatTimezoneDisplay(personalInfo.timezone) : "-"}
                            </span>
                        </div>
                        <div className="summary-item">
                            <span className="summary-label">Agent ID:</span>
                            <span className="summary-value">
                                {personalInfo.agentId || "-"}
                            </span>
                        </div>
                        <div className="summary-item">
                            <span className="summary-label">Position:</span>
                            <span className="summary-value">
                                {personalInfo.position || "-"}
                            </span>
                        </div>
                        <h3>{AppStrings.ROLE}</h3>
                        <div className="summary-item">
                            <span className="summary-label">{AppStrings.TYPE}:</span>
                            <span className="summary-value">
                                {selectedRoleData ? (
                                    <div className="role-summary">
                                        <div
                                            className="role-color-dot"
                                            style={{ backgroundColor: selectedRoleData.color ?? "#6b7280" }}
                                        />
                                        <span>{selectedRoleData.name}</span>
                                    </div>
                                ) : (
                                    "-"
                                )}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default NewStaff;

